/* ═══════════════════════════════════════════════════════════════
   Timesheets rebuild — the weekly grid (Tranche 2).

   Sections preserved exactly: Vacation & Holidays (read-only auto
   rows) → Overhead → Project Hours. Sticky header. Forecast ghosts
   render as muted placeholders in HourCell; "Accept week" here is
   the whole-week explicit-accept action (D7).
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { X, Plus, Check } from 'lucide-react';
import { SearchableSelect } from '../../components/SearchableSelect';
import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/Icon';
import { rem } from '../../lib/rem';
import { isArchived } from '../../lib/archive';
import type {
  HourEntry,
  HourForecast,
  Project,
  ActivityCode,
  OverheadCategory,
  ProjectPhase,
  VacationCategory,
  FirmHoliday,
  VacationRequest,
} from '../../../shared/types';
import type { ProjectRow } from './types';
import { HourCell } from './HourCell';
import { DAY_NAMES, formatMD } from './dateUtils';
import {
  buildVacationRows,
  getForecastForCell,
  getOverheadHours,
  getProjectHours,
  getProjectPhase,
  overheadCatTotal,
  rowHasUnacceptedGhost,
  rowProjectTotal,
} from './computeGrid';

const th: React.CSSProperties = {
  padding: `${rem(9)} ${rem(6)}`,
  fontSize: 'var(--font-size-sm)',
  fontWeight: 700,
  textAlign: 'center',
  borderBottom: '1px solid var(--bd)',
  color: 'var(--tm)',
  minWidth: rem(62),
  position: 'sticky',
  top: 0,
  background: 'var(--sa)',
  zIndex: 1,
};

const sectionLabel: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '.06em',
  color: 'var(--tm)',
  background: 'var(--sa)',
  borderBottom: '1px solid var(--bd)',
};

export interface TimesheetGridProps {
  weekDates: string[];
  todayStr: string;
  entries: HourEntry[];
  forecasts: HourForecast[];
  vacationRequests: VacationRequest[];
  firmHolidays: FirmHoliday[];
  vacationCategories: VacationCategory[];
  overheadCategories: OverheadCategory[];
  projectRows: ProjectRow[];
  projects: Project[];
  activityCodes: ActivityCode[];
  allPhases: ProjectPhase[];
  isLocked: boolean;
  onSaveProjectHours: (row: ProjectRow, dateISO: string, hours: number) => void;
  onSaveOverheadHours: (category: string, dateISO: string, hours: number) => void;
  onAddRow: () => void;
  onRemoveRow: (key: string) => void;
  onUpdateRow: (key: string, patch: Partial<ProjectRow>) => void;
  onAcceptRow: (row: ProjectRow) => void;
  onAcceptWeek: () => void;
}

export function TimesheetGrid({
  weekDates,
  todayStr,
  entries,
  forecasts,
  vacationRequests,
  firmHolidays,
  vacationCategories,
  overheadCategories,
  projectRows,
  projects,
  activityCodes,
  allPhases,
  isLocked,
  onSaveProjectHours,
  onSaveOverheadHours,
  onAddRow,
  onRemoveRow,
  onUpdateRow,
  onAcceptRow,
  onAcceptWeek,
}: TimesheetGridProps) {
  const overheadNames = overheadCategories.map((c) => c.name);
  const billableCodes = activityCodes.filter((c) => !c.name.startsWith('Z-'));
  // Archived projects drop out of the picker — except the one already on
  // this row, so an archived project already logged against never vanishes
  // out from under the entry.
  const projectOptionsFor = (currentId?: string) =>
    projects
      .filter((p) => !isArchived(p) || p.id === currentId)
      .map((p) => ({
        value: p.id,
        label: `${p.projectNumber ? p.projectNumber + ' — ' : ''}${p.name}`,
        hint: p.clientName,
      }));

  const vacationRows = buildVacationRows(
    vacationRequests,
    firmHolidays,
    vacationCategories,
    weekDates
  );
  const vacationDayTotals = weekDates.map((ds) =>
    vacationRows.reduce((sum, row) => sum + (row.hoursByDate[ds] || 0), 0)
  );
  const vacationWeekTotal = vacationDayTotals.reduce((s, t) => s + t, 0);

  const overheadDayTotals = weekDates.map((ds) =>
    overheadNames.reduce((sum, cat) => sum + getOverheadHours(entries, cat, ds), 0)
  );
  const overheadWeekTotal = overheadDayTotals.reduce((s, t) => s + t, 0);

  const projectDayTotals = weekDates.map((ds) =>
    projectRows.reduce(
      (sum, row) => sum + getProjectHours(entries, row.projectId, row.activityCodeId, ds),
      0
    )
  );
  const projectWeekTotal = projectDayTotals.reduce((s, t) => s + t, 0);

  const dayTotals = weekDates.map(
    (_, i) => overheadDayTotals[i] + projectDayTotals[i] + vacationDayTotals[i]
  );
  const weekTotal = dayTotals.reduce((s, t) => s + t, 0);

  const anyGhostUnaccepted = projectRows.some((row) =>
    rowHasUnacceptedGhost(entries, forecasts, row, weekDates)
  );

  return (
    <div
      style={{
        background: 'var(--sf)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--bd)',
        overflow: 'hidden',
        marginBottom: 'var(--space-4)',
      }}
    >
      {anyGhostUnaccepted && !isLocked && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--space-2)',
            padding: 'var(--space-2) var(--space-4)',
            background: 'color-mix(in srgb, var(--color-info) 8%, transparent)',
            borderBottom: '1px solid var(--bd)',
            fontSize: 'var(--font-size-base)',
            color: 'var(--tm)',
          }}
        >
          <span>Forecasted hours are shown as suggestions in empty cells.</span>
          <Button size="sm" variant="outline" onClick={onAcceptWeek}>
            <Icon icon={Check} em={1.3} /> Accept week
          </Button>
        </div>
      )}
      <div style={{ maxHeight: '100%', overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: rem(200) }} />
            <col style={{ width: rem(100) }} />
            <col style={{ width: rem(130) }} />
            {weekDates.map((_, i) => (
              <col key={i} style={{ width: rem(62) }} />
            ))}
            <col style={{ width: rem(56) }} />
            <col style={{ width: rem(34) }} />
          </colgroup>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left', padding: `${rem(9)} var(--space-3)` }}>
                Description
              </th>
              <th style={{ ...th, textAlign: 'left', padding: `${rem(9)} ${rem(6)}` }}>Phase</th>
              <th style={{ ...th, textAlign: 'left', padding: `${rem(9)} var(--space-2)` }}>
                Billing Code
              </th>
              {weekDates.map((ds, i) => (
                <th
                  key={ds}
                  style={{
                    ...th,
                    color: ds === todayStr ? 'var(--ac)' : 'var(--tm)',
                    background: ds === todayStr ? 'rgba(var(--acr),0.06)' : 'var(--sa)',
                  }}
                >
                  <div>{DAY_NAMES[i]}</div>
                  <div
                    style={{ fontSize: 'var(--font-size-xs)', fontWeight: 400, marginTop: rem(1) }}
                  >
                    {formatMD(ds)}
                  </div>
                </th>
              ))}
              <th style={th}>TOTAL</th>
              <th style={{ ...th, width: rem(34) }} />
            </tr>
          </thead>
          <tbody>
            {/* ── VACATION & HOLIDAYS (auto-populated, read-only) ── */}
            {vacationRows.length > 0 && (
              <>
                <tr>
                  <td colSpan={12} style={sectionLabel}>
                    Vacation &amp; Holidays
                  </td>
                </tr>
                {vacationRows.map((row, ri) => (
                  <tr
                    key={`vac-${ri}`}
                    style={{
                      borderBottom: '1px solid var(--bd)',
                      background: 'rgba(var(--okr),0.03)',
                    }}
                  >
                    <td
                      style={{
                        padding: `${rem(5)} var(--space-3)`,
                        fontSize: 'var(--font-size-base)',
                        fontStyle: 'italic',
                        color: 'var(--ok)',
                      }}
                    >
                      {row.label}
                    </td>
                    <td style={{ padding: `${rem(5)} ${rem(6)}` }} />
                    <td
                      style={{
                        padding: `${rem(5)} var(--space-2)`,
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--tm)',
                        fontStyle: 'italic',
                      }}
                    >
                      Non-billable
                    </td>
                    {weekDates.map((ds) => {
                      const hrs = row.hoursByDate[ds] || 0;
                      return (
                        <td
                          key={ds}
                          style={{
                            padding: `${rem(3)} ${rem(3)}`,
                            textAlign: 'center',
                            background: ds === todayStr ? 'rgba(var(--acr),0.03)' : undefined,
                          }}
                        >
                          <span
                            style={{
                              display: 'block',
                              padding: 'var(--space-1) 0',
                              fontSize: 'var(--font-size-base)',
                              fontFamily: "'JetBrains Mono', monospace",
                              color: hrs > 0 ? 'var(--ok)' : 'var(--tm)',
                            }}
                          >
                            {hrs || '–'}
                          </span>
                        </td>
                      );
                    })}
                    <td
                      style={{
                        padding: `${rem(5)} ${rem(6)}`,
                        textAlign: 'center',
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 'var(--font-size-base)',
                        fontWeight: 600,
                        color: row.total > 0 ? 'var(--ok)' : 'var(--tm)',
                      }}
                    >
                      {row.total || '–'}
                    </td>
                    <td />
                  </tr>
                ))}
                <tr
                  style={{
                    background: 'rgba(var(--okr),0.04)',
                    borderBottom: '2px solid var(--bd)',
                  }}
                >
                  <td
                    colSpan={3}
                    style={{
                      padding: `${rem(7)} var(--space-3)`,
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 700,
                      color: 'var(--ok)',
                    }}
                  >
                    VACATION SUBTOTAL
                  </td>
                  {vacationDayTotals.map((t, i) => (
                    <td
                      key={i}
                      style={{
                        padding: `${rem(7)} var(--space-1)`,
                        textAlign: 'center',
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 'var(--font-size-base)',
                        fontWeight: 700,
                        color: t > 0 ? 'var(--ok)' : 'var(--bd)',
                      }}
                    >
                      {t || '–'}
                    </td>
                  ))}
                  <td
                    style={{
                      padding: `${rem(7)} ${rem(6)}`,
                      textAlign: 'center',
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 'var(--font-size-base)',
                      fontWeight: 700,
                      color: vacationWeekTotal > 0 ? 'var(--ok)' : 'var(--bd)',
                    }}
                  >
                    {vacationWeekTotal || '–'}
                  </td>
                  <td />
                </tr>
              </>
            )}

            {/* ── OVERHEAD ── */}
            <tr>
              <td colSpan={12} style={sectionLabel}>
                Overhead / Non-Billable Hours
              </td>
            </tr>
            {overheadNames.map((cat) => {
              const catTotal = overheadCatTotal(entries, cat, weekDates);
              return (
                <tr key={cat} style={{ borderBottom: '1px solid var(--bd)' }}>
                  <td
                    style={{
                      padding: `${rem(5)} var(--space-3)`,
                      fontSize: 'var(--font-size-base)',
                    }}
                  >
                    {cat}
                  </td>
                  <td style={{ padding: `${rem(5)} ${rem(6)}` }} />
                  <td
                    style={{
                      padding: `${rem(5)} var(--space-2)`,
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--tm)',
                      fontStyle: 'italic',
                    }}
                  >
                    Non-billable
                  </td>
                  {weekDates.map((ds) => (
                    <td
                      key={ds}
                      style={{
                        padding: `${rem(3)} ${rem(3)}`,
                        background: ds === todayStr ? 'rgba(var(--acr),0.03)' : undefined,
                      }}
                    >
                      <HourCell
                        value={getOverheadHours(entries, cat, ds)}
                        disabled={isLocked}
                        isToday={ds === todayStr}
                        onCommit={(hours) => onSaveOverheadHours(cat, ds, hours)}
                        ariaLabel={`${cat} hours on ${ds}`}
                      />
                    </td>
                  ))}
                  <td
                    style={{
                      padding: `${rem(5)} ${rem(6)}`,
                      textAlign: 'center',
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 'var(--font-size-base)',
                      fontWeight: 600,
                      color: catTotal > 0 ? 'var(--tx)' : 'var(--tm)',
                    }}
                  >
                    {catTotal || '–'}
                  </td>
                  <td />
                </tr>
              );
            })}
            <tr
              style={{ background: 'rgba(var(--acr),0.025)', borderBottom: '2px solid var(--bd)' }}
            >
              <td
                colSpan={3}
                style={{
                  padding: `${rem(7)} var(--space-3)`,
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 700,
                  color: 'var(--tm)',
                }}
              >
                OVERHEAD SUBTOTAL
              </td>
              {overheadDayTotals.map((t, i) => (
                <td
                  key={i}
                  style={{
                    padding: `${rem(7)} var(--space-1)`,
                    textAlign: 'center',
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 'var(--font-size-base)',
                    fontWeight: 700,
                    color: t > 0 ? 'var(--tm)' : 'var(--bd)',
                  }}
                >
                  {t || '–'}
                </td>
              ))}
              <td
                style={{
                  padding: `${rem(7)} ${rem(6)}`,
                  textAlign: 'center',
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 700,
                  color: overheadWeekTotal > 0 ? 'var(--tm)' : 'var(--bd)',
                }}
              >
                {overheadWeekTotal || '–'}
              </td>
              <td />
            </tr>

            {/* ── PROJECT HOURS ── */}
            <tr>
              <td colSpan={12} style={sectionLabel}>
                Project Hours
              </td>
            </tr>
            {projectRows.map((row) => {
              const rowTotal = rowProjectTotal(entries, row, weekDates);
              const phase = getProjectPhase(allPhases, row.projectId);
              const rowGhost = rowHasUnacceptedGhost(entries, forecasts, row, weekDates);
              return (
                <tr key={row.key} style={{ borderBottom: '1px solid var(--bd)' }}>
                  <td style={{ padding: `var(--space-1) ${rem(6)} var(--space-1) ${rem(10)}` }}>
                    <SearchableSelect
                      value={row.projectId}
                      options={projectOptionsFor(row.projectId)}
                      onChange={(id) => onUpdateRow(row.key, { projectId: id })}
                      disabled={isLocked}
                      placeholder="Select project…"
                      searchPlaceholder="Search projects…"
                      style={{ fontSize: 'var(--font-size-sm)', padding: `${rem(5)} ${rem(6)}` }}
                    />
                  </td>
                  <td
                    style={{
                      padding: 'var(--space-1) var(--space-1)',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--tm)',
                    }}
                  >
                    {phase || '–'}
                  </td>
                  <td style={{ padding: 'var(--space-1) var(--space-1)' }}>
                    <SearchableSelect
                      value={row.activityCodeId}
                      options={billableCodes.map((c) => ({ value: c.id, label: c.name }))}
                      onChange={(v) => onUpdateRow(row.key, { activityCodeId: v })}
                      disabled={isLocked}
                      placeholder="-- none --"
                      searchPlaceholder="Search activity codes…"
                      style={{ fontSize: 'var(--font-size-sm)', padding: `${rem(5)} ${rem(6)}` }}
                    />
                  </td>
                  {weekDates.map((ds) => (
                    <td
                      key={ds}
                      style={{
                        padding: `${rem(3)} ${rem(3)}`,
                        background: ds === todayStr ? 'rgba(var(--acr),0.03)' : undefined,
                      }}
                    >
                      <HourCell
                        value={getProjectHours(entries, row.projectId, row.activityCodeId, ds)}
                        ghost={getForecastForCell(forecasts, row.projectId, ds)?.hours}
                        disabled={isLocked || !row.projectId}
                        isToday={ds === todayStr}
                        onCommit={(hours) => onSaveProjectHours(row, ds, hours)}
                        ariaLabel={`Hours on ${ds}`}
                      />
                    </td>
                  ))}
                  <td
                    style={{
                      padding: `${rem(5)} ${rem(6)}`,
                      textAlign: 'center',
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 'var(--font-size-base)',
                      fontWeight: 600,
                      color: rowTotal > 0 ? 'var(--tx)' : 'var(--tm)',
                    }}
                  >
                    {rowTotal || '–'}
                  </td>
                  <td style={{ padding: `${rem(3)} var(--space-1)`, textAlign: 'center' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: rem(2),
                        justifyContent: 'center',
                      }}
                    >
                      {rowGhost && !isLocked && (
                        <button
                          onClick={() => onAcceptRow(row)}
                          title="Accept forecasted hours for this row"
                          aria-label="Accept forecasted hours for this row"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--ac)',
                            cursor: 'pointer',
                            padding: 'var(--space-1)',
                            minWidth: 'var(--space-6)',
                            minHeight: 'var(--space-6)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Icon icon={Check} em={1.0769} />
                        </button>
                      )}
                      {!isLocked && (
                        <button
                          onClick={() => onRemoveRow(row.key)}
                          title="Remove row"
                          aria-label="Remove row"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--dg)',
                            cursor: 'pointer',
                            padding: 'var(--space-1)',
                            minWidth: 'var(--space-6)',
                            minHeight: 'var(--space-6)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0.7,
                          }}
                        >
                          <Icon icon={X} size="md" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!isLocked && (
              <tr>
                <td colSpan={12} style={{ padding: `${rem(7)} var(--space-3)` }}>
                  <button
                    onClick={onAddRow}
                    style={{
                      padding: `${rem(5)} var(--space-3)`,
                      minHeight: 'var(--space-6)',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px dashed var(--bd)',
                      background: 'transparent',
                      color: 'var(--ac)',
                      fontSize: 'var(--font-size-base)',
                      cursor: 'pointer',
                      fontWeight: 600,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 'var(--space-1)',
                    }}
                  >
                    <Icon icon={Plus} size="md" /> Add Project Row
                  </button>
                </td>
              </tr>
            )}
            <tr
              style={{ background: 'rgba(var(--acr),0.025)', borderBottom: '2px solid var(--bd)' }}
            >
              <td
                colSpan={3}
                style={{
                  padding: `${rem(7)} var(--space-3)`,
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 700,
                  color: 'var(--tm)',
                }}
              >
                PROJECT SUBTOTAL
              </td>
              {projectDayTotals.map((t, i) => (
                <td
                  key={i}
                  style={{
                    padding: `${rem(7)} var(--space-1)`,
                    textAlign: 'center',
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 'var(--font-size-base)',
                    fontWeight: 700,
                    color: t > 0 ? 'var(--tm)' : 'var(--bd)',
                  }}
                >
                  {t || '–'}
                </td>
              ))}
              <td
                style={{
                  padding: `${rem(7)} ${rem(6)}`,
                  textAlign: 'center',
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 700,
                  color: projectWeekTotal > 0 ? 'var(--tm)' : 'var(--bd)',
                }}
              >
                {projectWeekTotal || '–'}
              </td>
              <td />
            </tr>
            <tr style={{ background: 'var(--sa)', fontWeight: 700 }}>
              <td
                colSpan={3}
                style={{
                  padding: `${rem(10)} var(--space-3)`,
                  fontSize: 'var(--font-size-base)',
                  color: 'var(--tm)',
                }}
              >
                DAILY TOTAL
              </td>
              {dayTotals.map((t, i) => (
                <td
                  key={i}
                  style={{
                    padding: `${rem(10)} var(--space-1)`,
                    textAlign: 'center',
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 'var(--font-size-base)',
                    // 8h/day caution — token-level visual only, same threshold
                    // as before, no behavior gate (Tranche 2 sanctioned option).
                    color: t > 8 ? 'var(--color-warning)' : t > 0 ? 'var(--tx)' : 'var(--tm)',
                  }}
                >
                  {t || '–'}
                </td>
              ))}
              <td
                style={{
                  padding: `${rem(10)} ${rem(6)}`,
                  textAlign: 'center',
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: 700,
                  color: weekTotal > 40 ? 'var(--color-warning)' : 'var(--ac)',
                }}
              >
                {weekTotal}
              </td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function computeWeekTotals(
  entries: HourEntry[],
  forecasts: HourForecast[],
  vacationRequests: VacationRequest[],
  firmHolidays: FirmHoliday[],
  vacationCategories: VacationCategory[],
  overheadCategories: OverheadCategory[],
  projectRows: ProjectRow[],
  weekDates: string[]
) {
  const overheadNames = overheadCategories.map((c) => c.name);
  const vacationRows = buildVacationRows(
    vacationRequests,
    firmHolidays,
    vacationCategories,
    weekDates
  );
  const vacationWeekTotal = weekDates.reduce(
    (s, ds) => s + vacationRows.reduce((sum, row) => sum + (row.hoursByDate[ds] || 0), 0),
    0
  );
  const overheadWeekTotal = weekDates.reduce(
    (s, ds) => s + overheadNames.reduce((sum, cat) => sum + getOverheadHours(entries, cat, ds), 0),
    0
  );
  const projectWeekTotal = weekDates.reduce(
    (s, ds) =>
      s +
      projectRows.reduce(
        (sum, row) => sum + getProjectHours(entries, row.projectId, row.activityCodeId, ds),
        0
      ),
    0
  );
  const weekTotal = vacationWeekTotal + overheadWeekTotal + projectWeekTotal;
  return { weekTotal, overheadWeekTotal, projectWeekTotal, vacationWeekTotal };
}
